import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CreditCard,
  Download,
  FileText,
  ImageOff,
  ImagePlus,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Button } from "./ui/button";
import ReauthDialog from "./ReauthDialog";

const CATEGORIES = [
  { value: "insurance_card_front", label: "Insurance card — front", icon: CreditCard, imageFirst: true },
  { value: "insurance_card_back",  label: "Insurance card — back",  icon: CreditCard, imageFirst: true },
  { value: "drivers_license",      label: "Driver's license / ID",   icon: FileText,   imageFirst: true },
  { value: "referral_letter",      label: "Referral letter",         icon: FileText,   imageFirst: false },
  { value: "imaging_report",       label: "Imaging report",          icon: FileText,   imageFirst: false },
  { value: "intake_form",          label: "Signed intake form",      icon: FileText,   imageFirst: false },
  { value: "consent_receipt",      label: "Consent receipt",         icon: FileText,   imageFirst: false },
  { value: "other",                label: "Other document",          icon: FileText,   imageFirst: false },
];

function needsReauth(err) {
  if (err?.response?.status !== 401) return false;
  const detail = err.response?.data?.detail || "";
  return typeof detail === "string" && /re-auth/i.test(detail);
}

function isImage(doc) {
  return typeof doc?.content_type === "string" && doc.content_type.startsWith("image/");
}

// ---------------------------------------------------------------------------
// DocImageThumb
// ---------------------------------------------------------------------------
// PHI-safe inline thumbnail. Fetches the document over the authenticated
// download endpoint (which also emits an audit event), turns the response
// blob into an object URL, and revokes it on unmount. No raw PHI bytes ever
// sit in a query string or public URL — the <img> src is a process-local
// blob: URL only.
// ---------------------------------------------------------------------------
function DocImageThumb({ patientId, doc, onOpen }) {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [href, setHref] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setHref(null);

    api
      .get(`/patients/${patientId}/documents/${doc.id}/download`, {
        responseType: "blob",
      })
      .then((resp) => {
        if (cancelled) return;
        const url = URL.createObjectURL(resp.data);
        urlRef.current = url;
        setHref(url);
        setState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setState("error");
      });

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [patientId, doc.id]);

  if (state === "loading") {
    return (
      <div
        data-testid={`docs-thumb-loading-${doc.id}`}
        className="flex aspect-[16/10] w-full items-center justify-center rounded-sm border border-border bg-muted text-muted-foreground"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (state === "error" || !href) {
    return (
      <div
        data-testid={`docs-thumb-error-${doc.id}`}
        className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-1 rounded-sm border border-border bg-muted text-xs text-muted-foreground"
      >
        <ImageOff className="h-5 w-5" />
        <span>Preview unavailable</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={`docs-thumb-${doc.id}`}
      onClick={() => onOpen?.(doc)}
      className="group relative block w-full overflow-hidden rounded-sm border border-border bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      aria-label={`Open ${doc.filename} at full size`}
    >
      <img
        src={href}
        alt={doc.filename}
        loading="lazy"
        className="aspect-[16/10] w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
      />
      <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-foreground/60 px-2 py-1 text-[11px] text-background opacity-0 transition-opacity group-hover:opacity-100">
        <span className="truncate">{doc.filename}</span>
        <span className="font-mono">{Math.round((doc.size || 0) / 1024)} KB</span>
      </span>
    </button>
  );
}

/**
 * PatientDocumentsCard — insurance-card-first upload + inline thumbnails +
 * authenticated download + delete. Files are PHI — every preview, view, and
 * download streams through the backend which audits the access. Upload +
 * delete are reauth-gated; a 401 automatically pops the ReauthDialog and
 * retries.
 */
export default function PatientDocumentsCard({ patientId, canEdit }) {
  const [documents, setDocuments] = useState(null);
  const [uploading, setUploading] = useState(null); // `category` while uploading
  const [pendingDelete, setPendingDelete] = useState(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const pendingAction = useRef(null); // () => Promise — rerun after reauth
  const inputsRef = useRef({});

  const load = useCallback(async () => {
    setDocuments(null);
    try {
      const { data } = await api.get(`/patients/${patientId}/documents`);
      setDocuments(data || []);
    } catch {
      setDocuments([]);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  async function runWithReauth(fn) {
    try {
      await fn();
    } catch (err) {
      if (needsReauth(err)) {
        pendingAction.current = fn;
        setReauthOpen(true);
        return;
      }
      toast.error(formatApiError(err));
    }
  }

  async function upload(category, file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB cap");
      return;
    }
    setUploading(category);
    await runWithReauth(async () => {
      const body = new FormData();
      body.append("file", file);
      body.append("category", category);
      await api.post(`/patients/${patientId}/documents`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      load();
    });
    setUploading(null);
    if (inputsRef.current[category]) inputsRef.current[category].value = "";
  }

  async function download(doc) {
    try {
      const resp = await api.get(
        `/patients/${patientId}/documents/${doc.id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke later — browsers typically keep the blob alive until reload.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function remove(doc) {
    await runWithReauth(async () => {
      await api.delete(`/patients/${patientId}/documents/${doc.id}`);
      toast.success("Document removed");
      setPendingDelete(null);
      load();
    });
  }

  function onReauthConfirmed() {
    setReauthOpen(false);
    const fn = pendingAction.current;
    pendingAction.current = null;
    if (fn) fn().catch((err) => toast.error(formatApiError(err)));
  }

  const docsByCategory = (documents || []).reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});

  return (
    <section
      data-testid="patient-documents-card"
      className="rounded-sm border border-border bg-card p-6"
    >
      <div className="mb-4 border-b border-border pb-2">
        <h3 className="font-display text-lg font-medium text-foreground">
          Documents &amp; attachments
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Insurance cards, IDs, referral letters &amp; imaging reports. Previews
          and downloads stream from the authenticated backend and every access
          is audited.
        </p>
      </div>

      {documents === null ? (
        <div className="py-4 text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-6">
          {CATEGORIES.map(({ value, label, icon: Icon, imageFirst }) => {
            const items = docsByCategory[value] || [];
            const isInsurance = value.startsWith("insurance_card");
            if (!canEdit && items.length === 0) return null;
            return (
              <div key={value} data-testid={`docs-category-${value}`} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Icon className="h-4 w-4 text-primary" />
                    <span>{label}</span>
                    <span className="text-xs text-muted-foreground/70">({items.length})</span>
                  </div>
                  {canEdit && (
                    <>
                      <input
                        ref={(el) => { inputsRef.current[value] = el; }}
                        type="file"
                        accept={isInsurance ? "image/*" : "image/*,application/pdf"}
                        className="hidden"
                        data-testid={`docs-upload-${value}`}
                        onChange={(e) => upload(value, e.target.files?.[0])}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={uploading === value}
                        onClick={() => inputsRef.current[value]?.click()}
                        data-testid={`docs-upload-btn-${value}`}
                        className="rounded-sm"
                      >
                        {uploading === value ? (
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        ) : isInsurance ? (
                          <ImagePlus className="mr-2 h-3.5 w-3.5" />
                        ) : (
                          <Upload className="mr-2 h-3.5 w-3.5" />
                        )}
                        {items.length ? "Add another" : "Upload"}
                      </Button>
                    </>
                  )}
                </div>
                {items.length > 0 && (
                  imageFirst ? (
                    <ul
                      data-testid={`docs-grid-${value}`}
                      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                    >
                      {items.map((doc) => (
                        <li
                          key={doc.id}
                          data-testid={`docs-item-${doc.id}`}
                          className="overflow-hidden rounded-sm border border-border bg-background"
                        >
                          {isImage(doc) ? (
                            <DocImageThumb
                              patientId={patientId}
                              doc={doc}
                              onOpen={download}
                            />
                          ) : (
                            <div className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-1 bg-muted text-xs text-muted-foreground">
                              <FileText className="h-5 w-5" />
                              <span>Non-image file</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs">
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-foreground">{doc.filename}</div>
                              <div className="text-muted-foreground">
                                {doc.content_type} · {Math.round((doc.size || 0) / 1024)} KB ·{" "}
                                {new Date(doc.uploaded_at).toLocaleString()}
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => download(doc)}
                                data-testid={`docs-download-${doc.id}`}
                                className="h-7 px-2 text-primary hover:bg-primary/10"
                                aria-label={`Download ${doc.filename}`}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              {canEdit && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setPendingDelete(doc)}
                                  data-testid={`docs-delete-${doc.id}`}
                                  className="h-7 px-2 text-destructive hover:bg-destructive-soft"
                                  aria-label={`Delete ${doc.filename}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {items.map((doc) => (
                        <li
                          key={doc.id}
                          data-testid={`docs-item-${doc.id}`}
                          className="flex items-center justify-between gap-3 rounded-sm border border-border bg-background px-3 py-2 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-foreground">{doc.filename}</div>
                            <div className="text-xs text-muted-foreground">
                              {doc.content_type} · {Math.round((doc.size || 0) / 1024)} KB ·{" "}
                              {new Date(doc.uploaded_at).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => download(doc)}
                              data-testid={`docs-download-${doc.id}`}
                              className="h-7 px-2 text-primary hover:bg-primary/10"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            {canEdit && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setPendingDelete(doc)}
                                data-testid={`docs-delete-${doc.id}`}
                                className="h-7 px-2 text-destructive hover:bg-destructive-soft"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {pendingDelete && (
        <div
          data-testid="docs-delete-confirm"
          className="mt-5 flex items-start justify-between gap-4 rounded-sm border border-destructive-soft bg-destructive-soft px-4 py-3 text-sm text-destructive"
        >
          <span>
            Remove <strong>{pendingDelete.filename}</strong>? This soft-deletes the
            record; the raw file is retained for audit.
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setPendingDelete(null)}
              className="h-7 text-destructive hover:bg-destructive-soft"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => remove(pendingDelete)}
              data-testid="docs-delete-confirm-btn"
              className="h-7 rounded-sm bg-destructive hover:brightness-95"
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      <ReauthDialog
        open={reauthOpen}
        title="Confirm it's you"
        description="HIPAA policy requires step-up re-authentication before uploading or removing patient documents."
        onClose={() => {
          setReauthOpen(false);
          pendingAction.current = null;
        }}
        onConfirmed={onReauthConfirmed}
      />
    </section>
  );
}
